import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { printManifestoMinutaPdf } from '@/lib/manifestoMinutaPrint';
import { downloadManifestoMinutaDocx } from '@/lib/manifestoMinutaDocx';
import { entities, getDisplayMessage, uploadFile, parseUploadBlobIdFromFileUrl } from '@/api/apiClient';
import { useAuth } from '@/lib/AuthContext';
import { toast } from 'sonner';
import { Building2, FileSearch, Gavel, Loader2, Scale, Shield, Sparkles, FileUp, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';

const SLOT_META_RECURSO = {
  edital: { label: 'Edital (PDF)', order: 0 },
  termo_referencia: { label: 'Termo de Referência (PDF)', order: 1 },
  recurso: { label: 'Recurso administrativo (PDF)', order: 2 },
  outros: { label: 'Outros documentos (PDF)', order: 3 },
};

const SINGLE_SLOTS_RECURSO = ['edital', 'termo_referencia', 'recurso'];

function newId() {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `att-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function sortAttachmentsForIa(list) {
  const ready = list.filter((a) => a.file_url && !a.uploading);
  return [...ready].sort((a, b) => {
    const oa = SLOT_META_RECURSO[a.slot]?.order ?? 9;
    const ob = SLOT_META_RECURSO[b.slot]?.order ?? 9;
    if (oa !== ob) return oa - ob;
    return String(a.id).localeCompare(String(b.id));
  });
}

function resolveUploadFileUrlFromBlobId(blobId) {
  const env = typeof import.meta.env !== 'undefined' && import.meta.env.VITE_API_URL?.trim?.();
  if (env) return `${String(env).replace(/\/$/, '')}/api/upload/${blobId}`;
  return `/api/upload/${blobId}`;
}

const PHASES = [
  {
    key: 'documentos',
    title: 'Envie os Documentos',
    description: 'PDFs do edital, TR, recurso e outros; textos de apoio. Dados do edital e da empresa vêm do cadastro.',
    Icon: FileSearch,
  },
  {
    key: 'ia',
    title: 'IA Identifica Argumentos',
    description: 'Analisa os fatos e encontra os melhores fundamentos jurídicos.',
    Icon: Scale,
  },
  {
    key: 'revisao',
    title: 'Revise e Selecione',
    description: 'Escolha as teses mais adequadas ao seu caso concreto.',
    Icon: Gavel,
  },
  {
    key: 'protocolo',
    title: 'Baixe e Protocole',
    description: 'Minuta editável pronta para revisão e protocolo dentro do prazo.',
    Icon: Shield,
  },
];

const ARGUMENTS = [
  { id: 'a1', titulo: 'Inversão indevida da ordem de fases', confianca: 91, base: 'Art. 17, Lei 14.133/2021 · Acórdão TCU 2.853/2023' },
  { id: 'a2', titulo: 'Atestado técnico arbitrariamente desconsiderado', confianca: 96, base: 'Art. 67, §1º, Lei 14.133/2021' },
  { id: 'a3', titulo: 'Princípio do formalismo moderado', confianca: 88, base: 'Art. 12, Lei 14.133/2021 · Acórdão TCU 1.795/2022' },
];

function parseObservacoesRecurso(raw) {
  if (raw == null) return null;
  const s = typeof raw === 'string' ? raw.trim() : '';
  if (!s || s[0] !== '{') return null;
  try {
    const o = JSON.parse(s);
    if (o && typeof o === 'object' && o.tipo === 'recurso_fluxo' && o.v === 2) return { ...o, _v2Fluxo: true };
    if (o && typeof o === 'object' && o.tipo === 'recurso_administrativo' && o.v === 1) return o;
    return null;
  } catch {
    return null;
  }
}

function buildObservacoesRecursoV1({
  propostaResumo,
  motivoDesclassificacao,
  informacoesComplementares,
  referenciaNome,
  anexosIa,
  faseAtual,
  argumentosSelecionados,
  resumoTexto,
}) {
  const ids = Array.isArray(argumentosSelecionados) ? argumentosSelecionados : [...argumentosSelecionados];
  const anexos = Array.isArray(anexosIa) ? anexosIa : [];
  return JSON.stringify({
    v: 1,
    tipo: 'recurso_administrativo',
    salvoEm: new Date().toISOString(),
    argumentosSelecionados: ids,
    resumoTexto: resumoTexto ?? '',
    fluxoRecurso: {
      propostaResumo: String(propostaResumo ?? '').trim(),
      motivoDesclassificacao: String(motivoDesclassificacao ?? '').trim(),
      informacoesComplementares: String(informacoesComplementares ?? '').trim(),
      referenciaNome: referenciaNome ? String(referenciaNome).trim().slice(0, 400) : null,
      faseAtual: typeof faseAtual === 'number' ? faseAtual : 0,
      anexosIa: anexos,
    },
  });
}

function buildMinutaFromArguments(selected) {
  const base = ARGUMENTS.filter((a) => selected.has(a.id))
    .map((a, idx) => `${idx + 1}. ${a.titulo} (${a.base})`)
    .join('\n');
  return `I - Síntese fática\nA recorrente impugna a decisão de inabilitação.\n\nII - Fundamentos finais da peça\n${base || 'Sem argumentos selecionados.'}\n\nIII - Pedidos\n1. Recebimento e provimento do recurso.\n2. Reforma da decisão recorrida com reabilitação da empresa.`;
}

function Stepper({ currentIndex }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {PHASES.map((p, idx) => {
        const Icon = p.Icon;
        const active = idx === currentIndex;
        const done = idx < currentIndex;
        return (
          <div
            key={p.key}
            className={cn(
              'rounded-lg border p-3 text-left transition-shadow',
              active && 'ring-2 ring-blue-400 border-blue-300 shadow-md bg-blue-50/90',
              done && !active && 'bg-blue-50/50 border-blue-200',
              !done && !active && 'bg-muted/20 border-muted'
            )}
          >
            <div className="flex items-start gap-2">
              <span className="shrink-0 text-[11px] font-bold tabular-nums text-blue-800 pt-0.5">
                {String(idx + 1).padStart(2, '0')}
              </span>
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex items-center gap-1.5">
                  <Icon className={cn('h-4 w-4 shrink-0', active ? 'text-blue-700' : 'text-slate-500')} aria-hidden />
                  <p className="font-semibold text-xs leading-tight text-slate-900">{p.title}</p>
                </div>
                <p className="text-[11px] leading-snug text-slate-600">{p.description}</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function ManifestoRecursoFlow({
  onSalvarRascunho,
  onSalvarFinal,
  salvandoFinal = false,
  onAvancar,
  editalLabel,
  editalId,
  manifestacaoId,
}) {
  const queryClient = useQueryClient();
  const { empresa } = useAuth();
  const empresaIdConfig = empresa?.id;
  const fileInputRef = useRef(null);
  const pickSlotRef = useRef(null);
  const hydratedKeyRef = useRef('');

  const [stepIndex, setStepIndex] = useState(0);
  const [propostaResumo, setPropostaResumo] = useState('');
  const [motivoDesclassificacao, setMotivoDesclassificacao] = useState('');
  const [informacoesComplementares, setInformacoesComplementares] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [persistindoDocs, setPersistindoDocs] = useState(false);

  const [iaLoading, setIaLoading] = useState(false);
  const [iaConcluida, setIaConcluida] = useState(false);
  const [rascunhoIaTexto, setRascunhoIaTexto] = useState('');
  const [gerandoDocx, setGerandoDocx] = useState(false);

  const [selected, setSelected] = useState(() => new Set(['a1', 'a2', 'a3']));
  const [minutaText, setMinutaText] = useState('');

  const canUseApi = Boolean(editalId && manifestacaoId);

  const { data: editalDetalhe } = useQuery({
    queryKey: ['edital', editalId, 'manifesto-recurso'],
    queryFn: () => entities.Edital.get(editalId),
    enabled: Boolean(editalId),
  });

  const { data: configs = [] } = useQuery({
    queryKey: ['configuracao-empresa', empresaIdConfig],
    queryFn: () => entities.ConfiguracaoEmpresa.list(),
    enabled: Boolean(empresaIdConfig),
  });
  const empresaConfigRow = configs[0];

  const { data: detalheServidor } = useQuery({
    queryKey: ['manifesto', 'detail', editalId, manifestacaoId],
    queryFn: () => entities.Manifesto.getManifestacao(editalId, manifestacaoId),
    enabled: canUseApi,
  });

  const rehydrateKey = useMemo(() => {
    if (!detalheServidor || String(detalheServidor.id) !== String(manifestacaoId)) return '';
    const obs = typeof detalheServidor.observacoes === 'string' ? detalheServidor.observacoes.length : 0;
    const p0 = Array.isArray(detalheServidor.pecas) && detalheServidor.pecas.length ? detalheServidor.pecas[0] : null;
    return [manifestacaoId, detalheServidor.updatedAt ?? '', obs, p0?.versao ?? '', String(p0?.conteudo ?? '').slice(0, 80)].join('|');
  }, [detalheServidor, manifestacaoId]);

  useEffect(() => {
    if (!rehydrateKey || hydratedKeyRef.current === rehydrateKey) return;
    hydratedKeyRef.current = rehydrateKey;

    const det = detalheServidor;
    if (!det) return;

    const parsed = parseObservacoesRecurso(det.observacoes);
    if (parsed?._v2Fluxo) {
      setPropostaResumo(parsed.propostaResumo || '');
      setMotivoDesclassificacao(parsed.motivoDesclassificacao || '');
      if (typeof parsed.faseAtual === 'number' && parsed.faseAtual >= 0 && parsed.faseAtual <= 3) {
        setStepIndex(parsed.faseAtual);
      }
      if (Array.isArray(parsed.argumentosSelecionados) && parsed.argumentosSelecionados.length) {
        setSelected(new Set(parsed.argumentosSelecionados));
      }
    } else if (parsed && parsed.v === 1) {
      if (Array.isArray(parsed.argumentosSelecionados) && parsed.argumentosSelecionados.length) {
        setSelected(new Set(parsed.argumentosSelecionados));
      }
      const fr = parsed.fluxoRecurso;
      if (fr && typeof fr === 'object') {
        setPropostaResumo(fr.propostaResumo || '');
        setMotivoDesclassificacao(fr.motivoDesclassificacao || '');
        setInformacoesComplementares(fr.informacoesComplementares || '');
        if (Array.isArray(fr.anexosIa) && fr.anexosIa.length) {
          setAttachments(
            fr.anexosIa.map((row, idx) => ({
              id: `srv-${row.uploadBlobId}-${idx}`,
              slot: row.slot || 'outros',
              nome: row.nome || `Documento ${idx + 1}`,
              file_url: row.uploadBlobId ? resolveUploadFileUrlFromBlobId(row.uploadBlobId) : null,
              uploading: false,
              fromServer: true,
            }))
          );
        }
        if (typeof fr.faseAtual === 'number' && fr.faseAtual >= 0 && fr.faseAtual <= 3) {
          setStepIndex(fr.faseAtual);
        }
      }
    }

    const peca0 = Array.isArray(det.pecas) && det.pecas.length ? det.pecas[0] : null;
    if (peca0?.conteudo && String(peca0.conteudo).trim()) {
      const txt = String(peca0.conteudo);
      setMinutaText(txt);
      if (peca0.origem === 'ia') {
        setRascunhoIaTexto(txt);
        setIaConcluida(true);
      }
    }

    const faseSalva =
      parsed?.fluxoRecurso?.faseAtual ?? (parsed?._v2Fluxo && typeof parsed.faseAtual === 'number' ? parsed.faseAtual : null);
    if (typeof faseSalva === 'number' && faseSalva >= 2) {
      setIaConcluida(true);
    }

  }, [rehydrateKey, detalheServidor]);

  const uploadingAny = useMemo(() => attachments.some((a) => a.uploading), [attachments]);
  const hasRecursoPdf = useMemo(
    () => attachments.some((a) => a.slot === 'recurso' && a.file_url && !a.uploading),
    [attachments]
  );

  const avg = useMemo(() => {
    const arr = ARGUMENTS.filter((a) => selected.has(a.id)).map((a) => a.confianca);
    if (!arr.length) return 0;
    return Math.round(arr.reduce((s, x) => s + x, 0) / arr.length);
  }, [selected]);

  const resumo = useMemo(
    () => `Recurso administrativo — ${selected.size} tese(s) selecionada(s). Confiança média indicativa: ${avg}%.`,
    [selected, avg]
  );

  useEffect(() => {
    if (stepIndex !== 3) return;
    const t = window.setTimeout(() => {
      document.getElementById('manifesto-etapa-protocolo-recurso')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 150);
    return () => window.clearTimeout(t);
  }, [stepIndex]);

  const persistDocumentos = useCallback(async (faseAtual = 1) => {
    if (!canUseApi) {
      toast.error('Manifestação não identificada.');
      return false;
    }
    const sorted = sortAttachmentsForIa(attachments);
    const anexosIa = sorted
      .map((a) => {
        const uploadBlobId = parseUploadBlobIdFromFileUrl(a.file_url);
        if (!uploadBlobId) return null;
        return { slot: a.slot, uploadBlobId, nome: a.nome || null };
      })
      .filter(Boolean);
    const recursoAtt = sorted.find((a) => a.slot === 'recurso');
    const recursoBid = recursoAtt?.file_url ? parseUploadBlobIdFromFileUrl(recursoAtt.file_url) : null;
    const nomeRef = recursoAtt?.nome || sorted[0]?.nome || null;

    const obs = buildObservacoesRecursoV1({
      propostaResumo,
      motivoDesclassificacao,
      informacoesComplementares,
      referenciaNome: nomeRef,
      anexosIa,
      faseAtual,
      argumentosSelecionados: selected,
      resumoTexto: resumo,
    });
    const patch = { observacoes: obs };
    if (recursoBid) patch.referenciaUploadBlobId = recursoBid;
    setPersistindoDocs(true);
    try {
      await entities.Manifesto.patchManifestacao(editalId, manifestacaoId, patch);
      await queryClient.invalidateQueries({ queryKey: ['manifesto', 'detail', editalId, manifestacaoId] });
      return true;
    } catch (e) {
      toast.error(getDisplayMessage(e, 'Não foi possível gravar os documentos.'));
      return false;
    } finally {
      setPersistindoDocs(false);
    }
  }, [
    canUseApi,
    editalId,
    manifestacaoId,
    propostaResumo,
    motivoDesclassificacao,
    informacoesComplementares,
    attachments,
    selected,
    queryClient,
    resumo,
  ]);

  const executarIa = useCallback(async () => {
    if (!canUseApi) {
      toast.error('Manifestação não identificada.');
      return;
    }
    setIaLoading(true);
    try {
      await persistDocumentos(1);
      const out = await entities.Manifesto.gerarRascunhoIa(editalId, manifestacaoId);
      const conteudo = out?.peca?.conteudo ?? out?.manifestacao?.pecas?.[0]?.conteudo;
      if (typeof conteudo === 'string' && conteudo.trim()) {
        setRascunhoIaTexto(conteudo.trim());
        setIaConcluida(true);
        toast.success('Análise concluída. Fundamentos e rascunho disponíveis para revisão.');
      } else {
        toast.error('A IA não retornou texto utilizável.');
      }
      await queryClient.invalidateQueries({ queryKey: ['manifesto', 'detail', editalId, manifestacaoId] });
    } catch (e) {
      toast.error(getDisplayMessage(e, 'Falha na análise com IA.'));
    } finally {
      setIaLoading(false);
    }
  }, [canUseApi, editalId, manifestacaoId, persistDocumentos, queryClient]);

  function openFilePicker(slot) {
    pickSlotRef.current = slot;
    fileInputRef.current?.click();
  }

  async function handleFileSelected(ev) {
    const file = ev.target.files?.[0];
    ev.target.value = '';
    const slot = pickSlotRef.current;
    pickSlotRef.current = null;
    if (!file || !slot) return;
    const lower = file.name.toLowerCase();
    if (!lower.endsWith('.pdf')) {
      toast.error('Envie apenas arquivos PDF para análise pela IA.');
      return;
    }
    const nome = file.name;
    const id = newId();
    if (slot === 'outros') {
      setAttachments((prev) => [...prev, { id, slot, nome, file_url: null, uploading: true }]);
    } else {
      setAttachments((prev) => [...prev.filter((a) => a.slot !== slot), { id, slot, nome, file_url: null, uploading: true }]);
    }
    try {
      const { file_url } = await uploadFile(file);
      if (!file_url) throw new Error('Upload sem URL');
      setAttachments((prev) => prev.map((a) => (a.id === id ? { ...a, file_url, uploading: false } : a)));
      toast.success(`${SLOT_META_RECURSO[slot]?.label || 'Documento'} enviado.`);
    } catch (err) {
      setAttachments((prev) => prev.filter((a) => a.id !== id));
      toast.error(getDisplayMessage(err, 'Falha no envio do arquivo.'));
    }
  }

  function removeAttachment(id) {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  }

  function podeAvancarDeDocumentos() {
    const p = propostaResumo.trim();
    const m = motivoDesclassificacao.trim();
    return p.length >= 20 && m.length >= 20 && hasRecursoPdf && !uploadingAny;
  }

  async function handleAvancar() {
    if (stepIndex === 0) {
      if (!podeAvancarDeDocumentos()) {
        if (!hasRecursoPdf) {
          toast.error('Anexe o PDF do recurso administrativo (peça). Envie também os demais PDFs quando tiver.');
        } else if (uploadingAny) {
          toast.error('Aguarde o término dos envios de PDF.');
        } else {
          toast.error('Preencha proposta e motivo (mínimo 20 caracteres cada).');
        }
        return;
      }
      const ok = await persistDocumentos(1);
      if (!ok) return;
      setStepIndex(1);
      if (typeof onAvancar === 'function') onAvancar(1);
      return;
    }
    if (stepIndex === 1) {
      if (!iaConcluida) {
        toast.error('Execute a análise com IA antes de avançar.');
        return;
      }
      const ok = await persistDocumentos(2);
      if (!ok) return;
      setStepIndex(2);
      if (typeof onAvancar === 'function') onAvancar(2);
      return;
    }
    if (stepIndex === 2) {
      if (selected.size === 0) {
        toast.error('Selecione ao menos uma tese para a minuta.');
        return;
      }
      const ok = await persistDocumentos(3);
      if (!ok) return;
      setMinutaText((prev) => {
        const cur = String(prev ?? '').trim();
        if (cur) return prev;
        const ia = String(rascunhoIaTexto ?? '').trim();
        if (ia) return ia;
        return buildMinutaFromArguments(selected);
      });
      setStepIndex(3);
      if (typeof onAvancar === 'function') onAvancar(3);
    }
  }

  function handleVoltar() {
    if (stepIndex <= 0) return;
    setStepIndex((i) => i - 1);
  }

  const faseTitulo = PHASES[stepIndex]?.title ?? '';
  const faseDescricao = PHASES[stepIndex]?.description ?? '';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Gavel className="h-5 w-5 text-blue-600" />
          Recurso Administrativo
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <Stepper currentIndex={stepIndex} />

        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
          <p className="text-xs font-semibold text-blue-900">
            Fase {stepIndex + 1} de {PHASES.length} — {faseTitulo}
          </p>
          <p className="text-sm mt-1 text-slate-800">{faseDescricao}</p>
        </div>

        {!canUseApi ? (
          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3">
            Selecione uma manifestação de recurso na lista para habilitar envio de documentos e análise com IA.
          </p>
        ) : null}

        {stepIndex === 0 ? (
          <div className="space-y-4 rounded-lg border bg-card p-4">
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,application/pdf"
              className="hidden"
              onChange={handleFileSelected}
            />

            <div className="grid gap-3 md:grid-cols-2">
              <div className="rounded-md border border-slate-200 bg-slate-50/80 p-3 space-y-2">
                <Label className="text-xs font-semibold text-slate-800 flex items-center gap-1.5">
                  <FileSearch className="h-3.5 w-3.5" />
                  Dados do edital (sistema)
                </Label>
                <p className="text-xs text-slate-600">
                  Estes dados e o JSON completo do certame são enviados automaticamente à IA com os PDFs anexados.
                </p>
                <p className="text-sm font-medium text-slate-900 leading-snug">{editalLabel?.trim() || '—'}</p>
                {editalDetalhe ? (
                  <ul className="text-xs text-slate-700 space-y-1 list-disc pl-4">
                    {editalDetalhe.instituicao ? (
                      <li>
                        <span className="font-medium">Instituição:</span> {editalDetalhe.instituicao}
                      </li>
                    ) : null}
                    {editalDetalhe.numero_pregao || editalDetalhe.numeroPregao ? (
                      <li>
                        <span className="font-medium">Pregão:</span>{' '}
                        {editalDetalhe.numero_pregao || editalDetalhe.numeroPregao}
                      </li>
                    ) : null}
                    {editalDetalhe.modalidade ? (
                      <li>
                        <span className="font-medium">Modalidade:</span> {editalDetalhe.modalidade}
                      </li>
                    ) : null}
                    {editalDetalhe.objeto ? (
                      <li className="line-clamp-4">
                        <span className="font-medium">Objeto:</span> {String(editalDetalhe.objeto).slice(0, 400)}
                        {String(editalDetalhe.objeto).length > 400 ? '…' : ''}
                      </li>
                    ) : null}
                  </ul>
                ) : editalId ? (
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <Loader2 className="h-3 w-3 animate-spin" /> Carregando detalhes do edital…
                  </p>
                ) : null}
              </div>
              <div className="rounded-md border border-slate-200 bg-slate-50/80 p-3 space-y-2">
                <Label className="text-xs font-semibold text-slate-800 flex items-center gap-1.5">
                  <Building2 className="h-3.5 w-3.5" />
                  Dados da empresa (sistema)
                </Label>
                <p className="text-xs text-slate-600">Cadastro em Empresas → Dados da Empresa; usado na qualificação do rascunho.</p>
                {empresaConfigRow ? (
                  <ul className="text-xs text-slate-700 space-y-1 list-disc pl-4">
                    {(empresaConfigRow.razao_social || empresaConfigRow.razaoSocial) && (
                      <li>
                        <span className="font-medium">Razão social:</span>{' '}
                        {empresaConfigRow.razao_social || empresaConfigRow.razaoSocial}
                      </li>
                    )}
                    {empresaConfigRow.cnpj && (
                      <li>
                        <span className="font-medium">CNPJ:</span> {empresaConfigRow.cnpj}
                      </li>
                    )}
                    {(empresaConfigRow.endereco_completo || empresaConfigRow.enderecoCompleto) && (
                      <li className="line-clamp-3">
                        <span className="font-medium">Endereço:</span>{' '}
                        {empresaConfigRow.endereco_completo || empresaConfigRow.enderecoCompleto}
                      </li>
                    )}
                  </ul>
                ) : empresaIdConfig ? (
                  <p className="text-xs text-slate-500">Nenhum dado cadastral encontrado. Complete o cadastro da empresa.</p>
                ) : (
                  <p className="text-xs text-slate-500">Carregando…</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="recurso-proposta">Proposta / participação no certame</Label>
              <Textarea
                id="recurso-proposta"
                rows={4}
                className="text-sm"
                placeholder="Resuma a proposta apresentada, lances ou pontos relevantes da participação."
                value={propostaResumo}
                onChange={(e) => setPropostaResumo(e.target.value)}
                disabled={!canUseApi}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="recurso-motivo">Motivo da desclassificação ou inabilitação (fatos e decisão)</Label>
              <Textarea
                id="recurso-motivo"
                rows={4}
                className="text-sm"
                placeholder="Cole ou resuma o fundamento da administração: exigência não atendida, documento rejeitado, nota de julgamento etc."
                value={motivoDesclassificacao}
                onChange={(e) => setMotivoDesclassificacao(e.target.value)}
                disabled={!canUseApi}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="recurso-complementares">Informações complementares e de apoio à defesa</Label>
              <p className="text-xs text-slate-500">
                Este texto será analisado pela IA em conjunto com todos os PDFs anexados (edital, TR, recurso e outros).
              </p>
              <Textarea
                id="recurso-complementares"
                rows={4}
                className="text-sm"
                placeholder="Linha do tempo, e-mails, números de protocolo, testemunhos, jurisprudência que pretenda destacar (sem inventar dados), etc."
                value={informacoesComplementares}
                onChange={(e) => setInformacoesComplementares(e.target.value)}
                disabled={!canUseApi}
              />
            </div>

            <div className="rounded-md border border-blue-200 bg-blue-50/60 p-3 space-y-2">
              <p className="text-sm font-medium text-slate-900">Anexos em PDF para a IA</p>
              <p className="text-xs text-slate-700">
                Envie o <strong>Recurso administrativo</strong> (obrigatório) e, se possível, o <strong>Edital</strong>, o{' '}
                <strong>Termo de Referência</strong> e <strong>outros</strong> documentos. Todos os arquivos válidos são
                extraídos e enviados ao modelo para formular a resposta ao recurso.
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-1 md:grid-cols-2">
              {SINGLE_SLOTS_RECURSO.map((slot) => {
                const meta = SLOT_META_RECURSO[slot];
                const att = attachments.find((a) => a.slot === slot);
                return (
                  <div key={slot} className="rounded-md border border-slate-200 p-3 space-y-2">
                    <Label className="text-xs font-semibold text-slate-700">
                      {meta.label}
                      {slot === 'recurso' ? <span className="text-red-600"> *</span> : null}
                    </Label>
                    {att ? (
                      <div className="flex items-center justify-between gap-2 text-xs">
                        <span className="truncate text-slate-800" title={att.nome}>
                          {att.uploading ? (
                            <span className="flex items-center gap-1 text-slate-500">
                              <Loader2 className="h-3 w-3 animate-spin" />
                              Enviando…
                            </span>
                          ) : (
                            att.nome
                          )}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 shrink-0"
                          onClick={() => removeAttachment(att.id)}
                          disabled={att.uploading}
                        >
                          <Trash2 className="h-3.5 w-3.5 text-slate-500" />
                        </Button>
                      </div>
                    ) : null}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => openFilePicker(slot)}
                      disabled={!canUseApi || att?.uploading}
                    >
                      <FileUp className="h-4 w-4 mr-2" />
                      {att ? 'Substituir PDF' : 'Enviar PDF'}
                    </Button>
                  </div>
                );
              })}
            </div>
            <div className="rounded-md border border-slate-200 p-3 space-y-2">
              <Label className="text-xs font-semibold text-slate-700">{SLOT_META_RECURSO.outros.label}</Label>
              {attachments.filter((a) => a.slot === 'outros').length ? (
                <ul className="space-y-2">
                  {attachments
                    .filter((a) => a.slot === 'outros')
                    .map((att) => (
                      <li
                        key={att.id}
                        className="flex items-center justify-between gap-2 text-xs rounded bg-slate-50 px-2 py-1.5"
                      >
                        <span className="truncate" title={att.nome}>
                          {att.uploading ? (
                            <span className="flex items-center gap-1 text-slate-500">
                              <Loader2 className="h-3 w-3 animate-spin" />
                              Enviando…
                            </span>
                          ) : (
                            att.nome
                          )}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 shrink-0"
                          onClick={() => removeAttachment(att.id)}
                          disabled={att.uploading}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </li>
                    ))}
                </ul>
              ) : (
                <p className="text-xs text-slate-500">Nenhum documento adicional. Opcional.</p>
              )}
              <Button type="button" variant="outline" size="sm" onClick={() => openFilePicker('outros')} disabled={!canUseApi}>
                <FileUp className="h-4 w-4 mr-2" />
                Adicionar PDF
              </Button>
            </div>
          </div>
        ) : null}

        {stepIndex === 1 ? (
          <div className="space-y-4 rounded-lg border bg-card p-4">
            <p className="text-sm text-slate-700">
              A IA analisa o contexto do edital e da empresa no sistema, os textos da fase anterior, as informações
              complementares e o texto extraído de <strong>todos</strong> os PDFs anexados (edital, termo de referência,
              recurso administrativo e outros), gerando o rascunho da peça.
            </p>
            <Button
              type="button"
              className="gap-2"
              disabled={!canUseApi || iaLoading}
              onClick={() => executarIa()}
            >
              {iaLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              Identificar fundamentos com IA
            </Button>
            {iaConcluida ? (
              <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-900">
                Análise concluída. Na próxima fase você revisa as teses sugeridas; na última fase edita a minuta completa
                (incluindo o texto gerado pela IA, se desejar).
              </div>
            ) : null}
            {rascunhoIaTexto.trim() ? (
              <details className="rounded-md border bg-muted/20 p-3 text-sm">
                <summary className="cursor-pointer font-medium text-slate-800">Prévia do rascunho gerado (trecho)</summary>
                <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap text-xs text-slate-700 font-mono">
                  {rascunhoIaTexto.slice(0, 4000)}
                  {rascunhoIaTexto.length > 4000 ? '\n…' : ''}
                </pre>
              </details>
            ) : null}
          </div>
        ) : null}

        {stepIndex === 2 ? (
          <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
            <div className="grid gap-3">
              <p className="text-xs text-slate-600">
                Teses sugeridas com base típica em recursos administrativos — marque as que melhor se aplicam ao seu
                caso e complemente na minuta na fase seguinte.
              </p>
              {ARGUMENTS.map((item) => {
                const checked = selected.has(item.id);
                return (
                  <label key={item.id} className="rounded-lg border p-3 block cursor-pointer">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-2">
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(v) => {
                            const next = new Set(selected);
                            if (v) next.add(item.id);
                            else next.delete(item.id);
                            setSelected(next);
                          }}
                        />
                        <div>
                          <p className="font-medium text-sm">{item.titulo}</p>
                          <p className="text-xs text-slate-600 mt-1">{item.base}</p>
                        </div>
                      </div>
                      <Badge variant="secondary">Prioridade {item.confianca}%</Badge>
                    </div>
                  </label>
                );
              })}
            </div>
            <div className="space-y-3">
              <div className="rounded-lg border bg-slate-50 p-3">
                <p className="text-sm font-medium">Pedidos usuais</p>
                <ul className="text-xs text-slate-600 mt-2 space-y-1">
                  <li>Reformar decisão de inabilitação ou desclassificação</li>
                  <li>Reabilitar a recorrente no certame</li>
                  <li>Reconhecer tempestividade e formalismo moderado</li>
                </ul>
              </div>
            </div>
          </div>
        ) : null}

        {stepIndex === 3 ? (
          <div className="space-y-4">
            <div className="rounded-lg border bg-muted/20 p-3">
              <p className="text-sm font-medium">Minuta do recurso (editável)</p>
              <p className="text-xs text-slate-600 mt-1">
                Ajuste o texto antes de exportar. Inclui o rascunho da IA e/ou as teses selecionadas, conforme a origem do
                texto carregado.
              </p>
              <Textarea
                rows={12}
                className="mt-3 text-sm font-mono"
                value={minutaText}
                onChange={(e) => setMinutaText(e.target.value)}
                placeholder="Fundamentos, fatos e pedidos…"
              />
            </div>
            <div
              id="manifesto-etapa-protocolo-recurso"
              className="scroll-mt-6 rounded-lg border border-blue-200 bg-blue-50 p-3 space-y-3"
            >
              <p className="text-sm font-medium">Exportar e protocolar</p>
              <p className="text-xs text-slate-700">
                Baixe a minuta em Word ou PDF para conferência e protocolo no prazo.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={!minutaText.trim()}
                  onClick={async () => {
                    try {
                      await printManifestoMinutaPdf({
                        tipoLabel: 'Recurso Administrativo',
                        etapaLabel: 'Protocolo',
                        editalLabel,
                        minutaText,
                      });
                    } catch (e) {
                      toast.error(e?.message || 'Erro ao imprimir minuta.');
                    }
                  }}
                >
                  Imprimir / PDF
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={!minutaText.trim() || gerandoDocx}
                  className="gap-2"
                  onClick={async () => {
                    setGerandoDocx(true);
                    try {
                      await downloadManifestoMinutaDocx(minutaText, {
                        editalLabel,
                        tipoSuffix: 'recurso-administrativo',
                      });
                    } catch (e) {
                      toast.error(e?.message || 'Erro ao gerar DOCX.');
                    } finally {
                      setGerandoDocx(false);
                    }
                  }}
                >
                  {gerandoDocx ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Baixar DOCX
                </Button>
              </div>
            </div>
          </div>
        ) : null}

        <div className="flex flex-col gap-3 border-t pt-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
          <p className="min-w-0 w-full flex-1 text-xs text-slate-600 break-words [overflow-wrap:anywhere] leading-relaxed sm:pr-2">
            {resumo}
          </p>
          <div className="flex w-full shrink-0 flex-wrap gap-2 sm:w-auto sm:justify-end">
            <Button type="button" variant="outline" size="sm" onClick={handleVoltar} disabled={stepIndex <= 0}>
              Voltar
            </Button>
            <Button type="button" variant="secondary" size="sm" onClick={onSalvarRascunho}>
              Salvar rascunho
            </Button>
            <Button
              type="button"
              size="sm"
              className="gap-2"
              disabled={salvandoFinal || typeof onSalvarFinal !== 'function'}
              onClick={async () => {
                if (typeof onSalvarFinal !== 'function') return;
                if (!minutaText.trim() && selected.size === 0) {
                  toast.error('Avance até a minuta ou selecione teses antes de salvar.');
                  return;
                }
                try {
                  await onSalvarFinal({
                    minutaConteudo: minutaText,
                    observacoesPayload: {
                      v: 1,
                      tipo: 'recurso_administrativo',
                      salvoEm: new Date().toISOString(),
                      argumentosSelecionados: [...selected],
                      resumoTexto: resumo,
                      fluxoRecurso: {
                        propostaResumo: String(propostaResumo ?? '').trim(),
                        motivoDesclassificacao: String(motivoDesclassificacao ?? '').trim(),
                        informacoesComplementares: String(informacoesComplementares ?? '').trim(),
                        referenciaNome: (() => {
                          const r = attachments.find((a) => a.slot === 'recurso');
                          return r?.nome ? String(r.nome).trim().slice(0, 400) : null;
                        })(),
                        faseAtual: stepIndex,
                        anexosIa: sortAttachmentsForIa(attachments)
                          .map((a) => {
                            const uploadBlobId = parseUploadBlobIdFromFileUrl(a.file_url);
                            if (!uploadBlobId) return null;
                            return { slot: a.slot, uploadBlobId, nome: a.nome || null };
                          })
                          .filter(Boolean),
                      },
                    },
                  });
                } catch {
                  /* toast no pai */
                }
              }}
            >
              {salvandoFinal ? <Loader2 className="h-4 w-4 animate-spin shrink-0" /> : null}
              Salvar
            </Button>
            {stepIndex < 3 ? (
              <Button
                type="button"
                size="sm"
                onClick={handleAvancar}
                disabled={(stepIndex === 0 && !podeAvancarDeDocumentos()) || persistindoDocs || (stepIndex === 1 && !iaConcluida)}
              >
                {stepIndex === 0 && (persistindoDocs ? <Loader2 className="h-4 w-4 animate-spin" /> : null)}
                {stepIndex === 0 ? 'Continuar para IA' : stepIndex === 1 ? 'Continuar para revisão' : 'Continuar para minuta'}
              </Button>
            ) : null}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
